import React, { Fragment, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { PaginationControl } from "react-bootstrap-pagination-control";
import AxisSpinner from "../components/AxisSpinner";
import { AxisDateTimeFull } from "../util/AxisDateTime";
import { getStatusBadge } from "../util/RefereeStatusUtils";
import { UserRefereeDetailModal } from "../modals/UserRefereeDetailModal";
import { UserLink } from "../util/Links";
import { PageTitleController } from "../components/PageTitleController";
import { BsFileEarmarkTextFill } from "react-icons/bs";

const ReferencesTable = () => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [references, setReferences] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReferee, setSelectedReferee] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [fetchCount] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    document.title = "References";
  }, []);

  useEffect(() => {
    setOffset(0); // Reset to first page when filter changes
  }, [statusFilter]);

  useEffect(() => {
    fetchReferences();
  }, [statusFilter, offset]);

  const fetchReferences = async () => {
    try {
      setLoading(true);
      const params = {
        page: offset,
        size: fetchCount,
      };
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const config = {
        headers: { "content-type": "application/json" },
        params: params,
      };

      const response = await authAxios().get(
        "/api/backoffice/references/all",
        config
      );

      setReferences(response.data.content || []);
      setTotalCount(response.data.totalElements || 0);
      setTotalPages(Math.ceil((response.data.totalElements || 0) / fetchCount));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error fetching references:", error);
    }
  };

  const handleViewRefereeDetail = (referee) => {
    setSelectedReferee(referee);
    setShowDetailModal(true);
  };

  const handleModalClose = () => {
    setShowDetailModal(false);
    fetchReferences(); // Refresh after modal closes in case changes were made
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;

    return (
      <Fragment>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>USER</th>
              <th>REFEREE NAME</th>
              <th>REFEREE EMAIL</th>
              <th>STATUS</th>
              <th>CONTACTED</th>
              <th>CONTACTED BY</th>
              <th>LAST EVENT</th>
              <th>REJECTION REASON</th>
              <th>CREATED</th>
            </tr>
          </thead>
          <tbody>
            {references.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center text-muted">
                  No references found
                </td>
              </tr>
            ) : (
              references.map((referee) => (
                <tr
                  key={referee.id}
                  onClick={() => handleViewRefereeDetail(referee)}
                  style={{ cursor: "pointer" }}
                  className="hover-row"
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    {referee.user ? (
                      UserLink(referee.user)
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <strong>{referee.refereeName}</strong>
                  </td>
                  <td>{referee.refereeEmail}</td>
                  <td>{getStatusBadge(referee.status)}</td>
                  <td>
                    {referee.refereeContacted ? (
                      <Badge bg="success">Yes</Badge>
                    ) : (
                      <Badge bg="secondary">No</Badge>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {referee.refereeContactedByUser ? (
                      UserLink(referee.refereeContactedByUser)
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">
                        {referee.lastEvent || "-"}
                      </small>
                      {referee.lastEventAt && (
                        <div>
                          <small className="text-muted">
                            {AxisDateTimeFull(referee.lastEventAt)}
                          </small>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {referee.rejectionReason ? (
                      <small className="text-danger">
                        {referee.rejectionReason}
                      </small>
                    ) : (
                      <small className="text-muted">-</small>
                    )}
                  </td>
                  <td>
                    <small>{AxisDateTimeFull(referee.created)}</small>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        <div>
          <p>
            Page {offset + 1} of {totalPages}, showing {fetchCount} record(s)
            out of {totalCount}
          </p>
          <div>
            <PaginationControl
              page={offset + 1}
              between={3}
              total={totalCount}
              limit={fetchCount}
              changePage={(page) => {
                setOffset(page - 1);
              }}
              ellipsis={1}
            />
          </div>
        </div>
      </Fragment>
    );
  };

  return (
    <div>
      <Row className={"icon-text-wrapper"}>
        <PageTitleController
          title={"References"}
          icon={<BsFileEarmarkTextFill className={"icon-right-spacing"} />}
        />
      </Row>

      <Tabs
        id="references-table-tabs"
        activeKey={statusFilter}
        onSelect={(k) => setStatusFilter(k)}
        className="mb-3"
      >
        <Tab eventKey="all" title="All"></Tab>
        <Tab eventKey="created" title="Created"></Tab>
        <Tab eventKey="sent" title="Sent"></Tab>
        <Tab eventKey="completed" title="Completed"></Tab>
        <Tab eventKey="rejected" title="Rejected"></Tab>
        <Tab eventKey="error" title="Error"></Tab>
      </Tabs>

      <br />
      {SpinnerOrData(loading)}

      <UserRefereeDetailModal
        visible={showDetailModal}
        close={handleModalClose}
        referee={selectedReferee}
        user={selectedReferee?.user}
        onRefereeUpdate={fetchReferences}
      />
    </div>
  );
};

export default ReferencesTable;
